// Customer-specific configuration
export interface CustomerConfig {
  logoUrl: string;
  logoAlt: string;
  appTitle: string;
  primaryColor?: string;
  secondaryColor?: string;
}

// Campaign-specific configuration
export interface CampaignConfig {
  candidateName: string;
  campaignName: string;
  campaignTitle: string;
  consentText: string;
  campaignWebsite?: string;
  campaignVenmo?: string;
  campaignYoutube?: string;
  voterRegistrationUrl?: string;
  volunteerHotline?: string;
  supportEmail?: string;
  contactEmail?: string;
  campaignEmail?: string;
  emailFromAddress?: string;
}

// Display brand names shown in UI (and used as the neutral prefix for the
// donation embed id/channel). The democrat build shares the VoteCivic brand —
// no partisan qualifier leaks into user-facing output. The registered legal
// entity used in the Terms / Privacy Policy is SUBSIDIARY_LEGAL_ENTITY_NAMES.
const SUBSIDIARY_TITLES: Record<string, string> = {
  nonpartisan: 'VoteCivic',
  gop: 'SignalVote',
  democrat: 'VoteCivic',
};

// Browser tab / in-app display titles. Democrat shares the VoteCivic brand
// in UI; the "Democrat" suffix is retained only for legal entity identification.
const SUBSIDIARY_APP_TITLES: Record<string, string> = {
  nonpartisan: 'VoteCivic',
  gop: 'SignalVote',
  democrat: 'VoteCivic',
};

const SUBSIDIARY_CONTACT_EMAILS: Record<string, string> = {
  nonpartisan: 'support@votecivic.com',
  gop: 'support@signalvote.com',
  democrat: 'support@votecivic.com',
};

// Registered legal-entity names used in the body of the Terms of Service and
// Privacy Policy. Distinct from the display brand (companyName): the nonpartisan
// and democrat builds are both operated by VoteCivic, LLC; the GOP build is
// SignalVote. NOTE: SignalVote's exact legal entity name and governing-law state
// are assumed to mirror VoteCivic, LLC (Alabama) — confirm with counsel.
const SUBSIDIARY_LEGAL_ENTITY_NAMES: Record<string, string> = {
  nonpartisan: 'VoteCivic, LLC',
  gop: 'SignalVote, LLC',
  democrat: 'VoteCivic, LLC',
};

const SUBSIDIARY_WEBSITE_DOMAINS: Record<string, string> = {
  nonpartisan: 'votecivic.com',
  gop: 'signalvote.com',
  democrat: 'votecivic.com',
};

// Governing law / exclusive venue for the Terms of Service. Both entities are
// Alabama-organized (Jefferson County venue) per current counsel-drafted terms.
const SUBSIDIARY_GOVERNING_LAW: Record<string, { state: string; venue: string }> = {
  nonpartisan: { state: 'Alabama', venue: 'Jefferson County, Alabama' },
  gop: { state: 'Alabama', venue: 'Jefferson County, Alabama' },
  democrat: { state: 'Alabama', venue: 'Jefferson County, Alabama' },
};

// The vote goal calculator and its home card, per brand. Set a brand to false to hide both.
// Nonpartisan has no app of its own.
const SUBSIDIARY_VOTE_GOAL: Record<string, boolean> = {
  nonpartisan: false,
  gop: true,
  democrat: true,
};

export function isVoteGoalOn(subsidiaryCode: string = import.meta.env.VITE_SUBSIDIARY || 'nonpartisan'): boolean {
  return SUBSIDIARY_VOTE_GOAL[subsidiaryCode] ?? false;
}

function getSubsidiaryTitle(): string {
  const code = import.meta.env.VITE_SUBSIDIARY || 'nonpartisan';
  return SUBSIDIARY_APP_TITLES[code] || 'VoteCivic';
}

// Get customer configuration from environment variables
export const getCustomerConfig = (): CustomerConfig => {
  return {
    logoUrl: import.meta.env.VITE_LOGO_URL || '',
    logoAlt: import.meta.env.VITE_LOGO_ALT || 'VoteCivic',
    appTitle: import.meta.env.VITE_TITLE || getSubsidiaryTitle(),
    primaryColor: import.meta.env.VITE_PRIMARY_COLOR,
    secondaryColor: import.meta.env.VITE_SECONDARY_COLOR,
  };
};

// Get campaign configuration from environment variables
export const getCampaignConfig = (): CampaignConfig => {
  return {
    candidateName: import.meta.env.VITE_CANDIDATE_NAME || '[VITE_CANDIDATE_NAME]',
    campaignName: import.meta.env.VITE_CAMPAIGN_NAME || '[VITE_CAMPAIGN_NAME]',
    campaignTitle: import.meta.env.VITE_CAMPAIGN_TITLE || '[VITE_CAMPAIGN_TITLE]',
    consentText: import.meta.env.VITE_CONSENT_TEXT || '[VITE_CONSENT_TEXT]',
    campaignWebsite: import.meta.env.VITE_CAMPAIGN_WEBSITE,
    campaignVenmo: import.meta.env.VITE_CAMPAIGN_VENMO,
    campaignYoutube: import.meta.env.VITE_CAMPAIGN_YOUTUBE,
    voterRegistrationUrl: import.meta.env.VITE_VOTER_REGISTRATION_URL,
    volunteerHotline: import.meta.env.VITE_VOLUNTEER_HOTLINE,
    supportEmail: import.meta.env.VITE_SUPPORT_EMAIL,
    contactEmail: import.meta.env.VITE_CONTACT_EMAIL || import.meta.env.VITE_SUPPORT_EMAIL,
    campaignEmail: import.meta.env.VITE_CAMPAIGN_EMAIL,
    emailFromAddress: import.meta.env.VITE_EMAIL_FROM_ADDRESS,
  };
};

export interface LegalConfig {
  /** Display brand shown in UI (e.g. "SignalVote", "VoteCivic"). */
  companyName: string;
  /** Registered legal entity named as "Company" in the Terms / Privacy Policy. */
  legalEntityName: string;
  contactEmail: string;
  websiteDomain: string;
  governingLawState: string;
  venue: string;
}

export function getLegalConfig(subsidiaryCode?: string): LegalConfig {
  const code = subsidiaryCode || import.meta.env.VITE_SUBSIDIARY || 'nonpartisan';
  const law = SUBSIDIARY_GOVERNING_LAW[code] || SUBSIDIARY_GOVERNING_LAW.nonpartisan;
  return {
    companyName: SUBSIDIARY_TITLES[code] || 'VoteCivic',
    legalEntityName: SUBSIDIARY_LEGAL_ENTITY_NAMES[code] || 'VoteCivic, LLC',
    contactEmail: SUBSIDIARY_CONTACT_EMAILS[code] || 'support@votecivic.com',
    websiteDomain: SUBSIDIARY_WEBSITE_DOMAINS[code] || 'votecivic.com',
    governingLawState: law.state,
    venue: law.venue,
  };
}

export const customerConfig = getCustomerConfig();
export const campaignConfig = getCampaignConfig();
export const legalConfig = getLegalConfig();
